import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { PopoverTraceBorder } from '@components/primitives/Popover';
import {
  type HoverPopoverAnchorReference,
  HoverPopoverSurface,
} from '@components/ui/HoverPopover';
import { LabelIconRow } from '@features/appointment-labels';
import { useColorFromEnum } from '@features/appointment-types';
import { AppointmentPropertiesTable } from '@features/appointments/components/AppointmentPropertiesTable';
import { formatTimeRange } from '@features/appointments/utils/dateTimeFormatters';
import { ReadinessIndicator } from '@features/eligibility-checks/components/ReadinessIndicator';
import { useEligibilityActions } from '@features/eligibility-checks/hooks/useEligibilityActions';
import {
  ELIG_TOKENS,
  getReadinessTreatment,
  READINESS_FILLED,
  READINESS_TONE,
} from '@features/eligibility-checks/theme/readinessTokens';
import type { BlockReadiness } from '@features/eligibility-checks/types/readiness';
import { ProviderBadge } from '@jotpsych/ui';
import CheckIcon from '@mui/icons-material/Check';
import OpenInFullOutlined from '@mui/icons-material/OpenInFullOutlined';
import PhoneOutlined from '@mui/icons-material/PhoneOutlined';
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined';
import StickyNote2Outlined from '@mui/icons-material/StickyNote2Outlined';
import { Box, Button, IconButton, Link, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { formatPhoneForDisplay } from '@utils/patientUtils';
import { motion } from 'motion/react';

import { useProviderNameMap } from '../../../hooks/useProviderNameMap';
import { getVisitStatusDisplay } from '../../../theme/visitStatus';
import type { CalendarEvent } from '../types';
import { getCalendarSessionCue } from '../utils/sessionCue';

import { getEventIcon } from './EventComponent/utils/getEventIcon';

const EVENT_BLOCK_BORDER = '1.5px solid rgba(83, 89, 118, 0.36)';

interface CalendarV2EventComponentProps {
  event: CalendarEvent;
  viewMode?: string;
  onOpenAppointmentDetail: (appointmentId: string) => void;
  isPopoverOpen?: boolean;
  onPopoverMouseEnter?: (event: CalendarEvent, anchorEl: HTMLElement) => void;
  onPopoverMouseLeave?: (appointmentId: string) => void;
}

export const CalendarV2EventComponent = ({
  event,
  viewMode,
  onOpenAppointmentDetail,
  isPopoverOpen,
  onPopoverMouseEnter,
  onPopoverMouseLeave,
}: CalendarV2EventComponentProps) => {
  const isTouch = useMediaQuery('(hover: none), (pointer: coarse)');
  const model = useCalendarV2EventModel(event);
  const sessionCue = getCalendarSessionCue(event.resource);

  const openDetail = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    onOpenAppointmentDetail(event.resource.appointment_id);
  };

  return (
    <Box
      data-calendar-v2-event-block="true"
      onClick={openDetail}
      onMouseEnter={e => {
        if (!isTouch) onPopoverMouseEnter?.(event, e.currentTarget);
      }}
      onMouseLeave={() => {
        if (!isTouch) onPopoverMouseLeave?.(event.resource.appointment_id);
      }}
      sx={{
        height: '100%',
        width: '100%',
        position: 'relative',
        display: 'flex',
        gap: 1,
        overflow: 'hidden',
        borderRadius: '6px',
        pl: `calc(${model.stripeWidth}px + 10px)`,
        pr: 1.25,
        py: 0.75,
        boxSizing: 'border-box',
        cursor: 'pointer',
        color: model.textColor,
        bgcolor: model.backgroundColor,
        border: EVENT_BLOCK_BORDER,
        outline: `${model.outlineWidth}px ${model.outlineStyle} ${model.accentColor}`,
        outlineOffset: `-${model.outlineWidth}px`,
        boxShadow: isPopoverOpen ? '0 6px 16px rgba(15, 23, 42, 0.12)' : model.shadow,
        transition: 'box-shadow 120ms ease-out, outline-width 120ms ease-out',
        '&:hover': {
          boxShadow: '0 6px 16px rgba(15, 23, 42, 0.12)',
          zIndex: 10,
        },
        '@container (height < 27px)': {
          py: 'clamp(1px, 15cqh, 6px)',
          lineHeight: 1.2,
        },
      }}
    >
      {model.stripeWidth > 0 && (
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            insetBlock: 0,
            left: 0,
            width: model.stripeWidth,
            bgcolor: model.stripeColor,
            pointerEvents: 'none',
          }}
        />
      )}
      <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
          {model.providerLabel && <ProviderBadge name={model.providerLabel} />}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              lineHeight: 1.25,
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            {event.title}
          </Box>
        </Box>
        {viewMode !== 'month' && (
          <Box
            sx={{
              mt: 0.25,
              fontSize: 11,
              opacity: 0.8,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              '@container (height < 27px)': { display: 'none' },
            }}
          >
            {formatTimeRange(event.start, event.end)}
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'auto auto',
          gap: 0.5,
          alignSelf: 'flex-start',
          flexShrink: 0,
          '@container (height < 45px)': {
            display: 'flex',
            alignItems: 'center',
          },
        }}
      >
        {model.visitStatus.showIconOnCard && (
          <model.visitStatus.icon
            aria-label={model.visitStatus.label}
            sx={{ fontSize: 14, color: model.visitStatus.color, opacity: 0.9 }}
          />
        )}
        {sessionCue && (
          <Tooltip title={sessionCue.label} placement="top" arrow>
            <sessionCue.Icon
              aria-label={sessionCue.label}
              sx={{
                fontSize: 14,
                opacity: sessionCue.useJotMeet ? 0.95 : 0.7,
                color: sessionCue.useJotMeet ? model.accentColor : undefined,
              }}
            />
          </Tooltip>
        )}
        {event.resource.notes && <StickyNote2Outlined sx={{ fontSize: 14, opacity: 0.7 }} />}
        {event.resource.labels && event.resource.labels.length > 0 && (
          <LabelIconRow labels={event.resource.labels} max={2} size={13} />
        )}
        {model.isConfirmed && <CheckIcon sx={{ fontSize: 14, opacity: 0.85 }} />}
        {model.readiness && (
          <Box sx={{ gridColumn: 2, gridRow: 1, order: 1, display: 'flex' }}>
            <ReadinessIndicator readiness={model.readiness} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

interface CalendarV2EventModel {
  actionMessage: string | null;
  accentColor: string;
  backgroundColor: string;
  isConfirmed: boolean;
  outlineStyle: 'solid' | 'dashed';
  outlineWidth: number;
  providerLabel: string | null;
  readiness: BlockReadiness | null;
  readinessLabel: string | null;
  shadow: string;
  stripeColor: string;
  stripeWidth: number;
  textColor: string;
  visitStatus: ReturnType<typeof getVisitStatusDisplay>;
}

const useCalendarV2EventModel = (event: CalendarEvent): CalendarV2EventModel => {
  const baseColor = useColorFromEnum(event.resource.color);
  const providerNameMap = useProviderNameMap();
  const blockState = event.resource.block_state;
  const readiness = blockState?.readiness ?? null;
  const readinessTreatment = getReadinessTreatment(readiness);
  const reality = blockState?.reality ?? getLegacyReality(event);
  const encounterStatus = (event.resource.encounter as { status?: string } | null)?.status;
  const isPast = encounterStatus === 'completed' || event.resource.no_showed === true;

  const providerLabel = event.resource.provider_id
    ? (event.resource.provider?.name ?? providerNameMap.get(event.resource.provider_id) ?? null)
    : null;

  const accentColor = useMemo(() => {
    if (reality === 'cancelled') return ELIG_TOKENS.red;
    if (reality === 'rescheduling') return '#CA8A04';
    if (readiness) return ELIG_TOKENS[READINESS_TONE[readiness]];
    return baseColor;
  }, [baseColor, readiness, reality]);

  const variant = getVariant(reality);

  return {
    actionMessage: getActionMessage(reality),
    accentColor,
    backgroundColor: opaqueTint(baseColor, isPast ? 0.03 : 0.06),
    isConfirmed:
      blockState?.qualifiers?.confirmed === true ||
      event.resource.confirmation_status === 'confirmed',
    outlineStyle: variant === 'suggested' ? 'dashed' : 'solid',
    outlineWidth: variant === 'standard' ? 0 : 2,
    providerLabel,
    readiness,
    readinessLabel: blockState?.readiness_label ?? null,
    shadow: variant === 'standard' ? 'none' : '0 4px 14px rgba(15, 23, 42, 0.10)',
    stripeColor: readinessTreatment?.color ?? 'transparent',
    stripeWidth: readinessTreatment?.stripeWidthPx ?? 0,
    textColor: isPast ? ELIG_TOKENS.inkSoft : ELIG_TOKENS.ink,
    visitStatus: getVisitStatusDisplay(
      event.resource.no_showed ? 'no_show' : event.resource.visit_status,
    ),
  };
};

interface CalendarV2AppointmentPopoverProps {
  event: CalendarEvent;
  anchorReference: CalendarV2PopoverAnchorReference;
  open: boolean;
  onClose: () => void;
  onPointerEnter: () => void;
  onPointerLeave: () => void;
  onInteractionPin: () => void;
  onOpenAppointmentDetail: (appointmentId: string) => void;
}

/** Calendar-facing alias for the shared hover-popover anchor reference. */
export type CalendarV2PopoverAnchorReference = HoverPopoverAnchorReference;

export const CalendarV2AppointmentPopover = ({
  event,
  anchorReference,
  open,
  onClose,
  onPointerEnter,
  onPointerLeave,
  onInteractionPin,
  onOpenAppointmentDetail,
}: CalendarV2AppointmentPopoverProps) => {
  const [isPopoverHovered, setIsPopoverHovered] = useState(false);
  const { openInvoice } = useEligibilityActions();
  const model = useCalendarV2EventModel(event);

  useEffect(() => {
    if (!open) setIsPopoverHovered(false);
  }, [open]);

  const openDetail = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    onClose();
    onOpenAppointmentDetail(event.resource.appointment_id);
  };

  const sendInvoice = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClose();
    openInvoice({
      patientId: event.resource.patient?.patient_id,
      patientName: event.resource.patient?.name ?? event.resource.display_title ?? undefined,
      encounterId: (event.resource.encounter as { encounter_id?: string } | null | undefined)
        ?.encounter_id,
      appointmentId: event.resource.appointment_id,
      memberObligationCents:
        event.resource.block_state?.readiness_inputs?.eligibility?.member_obligation_cents ?? null,
      source: 'calendar_block',
    });
  };

  return (
    <HoverPopoverSurface
      open={open}
      anchorReference={anchorReference}
      onClose={onClose}
      onPointerEnter={() => {
        setIsPopoverHovered(true);
        onPointerEnter();
      }}
      onPointerLeave={() => {
        setIsPopoverHovered(false);
        onPointerLeave();
      }}
      onInteractionPin={onInteractionPin}
      placement="right-start"
      // Keep the card open while editing an appointment property or sliding to
      // another event block — only true outside-presses dismiss it.
      outsidePress={pressEvent =>
        !isAppointmentPropertyEditorSurface(pressEvent.target) &&
        !isCalendarV2EventBlock(pressEvent.target)
      }
    >
      <CalendarV2ExpandedCard
        key={event.resource.appointment_id}
        event={event}
        model={model}
        isPopoverHovered={isPopoverHovered}
        onOpenDetail={openDetail}
        onSendInvoice={sendInvoice}
        onEditorInteraction={onInteractionPin}
      />
    </HoverPopoverSurface>
  );
};

const CalendarV2ExpandedCard = ({
  event,
  model,
  isPopoverHovered,
  onOpenDetail,
  onSendInvoice,
  onEditorInteraction,
}: {
  event: CalendarEvent;
  model: CalendarV2EventModel;
  isPopoverHovered: boolean;
  onOpenDetail: (e?: React.MouseEvent) => void;
  onSendInvoice: (e: React.MouseEvent) => void;
  onEditorInteraction: () => void;
}) => {
  const tracking = useEventTracking();
  const Icon = getEventIcon(event);
  const patientId = event.resource.patient?.patient_id;
  const appointmentId = event.resource.appointment_id;
  const phoneRaw = event.resource.patient?.telephone?.trim();
  const phoneDisplay = formatPhoneForDisplay(phoneRaw);
  const readinessTone = model.readiness ? READINESS_TONE[model.readiness] : null;
  const readinessColor = readinessTone ? ELIG_TOKENS[readinessTone] : model.accentColor;
  const readinessFilled = model.readiness ? READINESS_FILLED[model.readiness] : false;
  const readinessIsVerified = model.readiness === 'verified';

  return (
    <Box
      component={motion.div}
      animate={{
        boxShadow: isPopoverHovered
          ? '0 12px 30px rgba(0, 0, 0, 0.17)'
          : '0 8px 24px rgba(0, 0, 0, 0.12)',
      }}
      transition={{ duration: 0.12, ease: 'easeOut' }}
      sx={{
        position: 'relative',
        // Flexible width: grow to fit content (so time/date rows aren't
        // truncated), bounded by the original 320 floor and a cap so long notes
        // don't make the card huge.
        width: 'fit-content',
        minWidth: 320,
        maxWidth: 'min(90vw, 520px)',
        borderRadius: '8px',
        bgcolor: 'background.paper',
        color: ELIG_TOKENS.ink,
        border: `1px solid ${model.accentColor}`,
        overflow: 'visible',
      }}
    >
      <PopoverTraceBorder color={model.accentColor} active={isPopoverHovered} />
      <Tooltip title="Open full detail" placement="left" arrow>
        <IconButton
          size="small"
          onClick={onOpenDetail}
          aria-label="Open full detail"
          sx={{
            position: 'absolute',
            top: 6,
            right: 6,
            width: 24,
            height: 24,
            opacity: isPopoverHovered ? 1 : 0,
            transition: 'opacity 120ms ease-out',
            pointerEvents: isPopoverHovered ? 'auto' : 'none',
            color: ELIG_TOKENS.inkSoft,
          }}
        >
          <OpenInFullOutlined sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>

      <Box sx={{ display: 'flex', gap: 1, p: 2.5, pr: 4 }}>
        {Icon && <Icon sx={{ color: model.accentColor, fontSize: 24, flexShrink: 0 }} />}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {model.readiness && model.readinessLabel && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  flexShrink: 0,
                  ...(readinessFilled
                    ? { bgcolor: readinessColor }
                    : { border: `1.5px solid ${readinessColor}`, bgcolor: 'transparent' }),
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: readinessColor,
                  fontWeight: readinessIsVerified ? 500 : 700,
                  ...(readinessIsVerified
                    ? {}
                    : {
                        textDecoration: 'underline',
                        textDecorationThickness: '1.5px',
                      }),
                }}
              >
                {model.readinessLabel}
              </Typography>
              {model.readiness === 'collect' && (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<ReceiptOutlinedIcon sx={{ fontSize: 14 }} />}
                  onClick={onSendInvoice}
                  sx={{
                    ml: 'auto',
                    textTransform: 'none',
                    fontSize: 12,
                    py: 0.25,
                    minHeight: 0,
                    bgcolor: ELIG_TOKENS.midnight,
                    color: '#fff',
                    '&:hover': { bgcolor: ELIG_TOKENS.midnight, filter: 'brightness(0.9)' },
                  }}
                >
                  Send invoice
                </Button>
              )}
            </Box>
          )}
          <Typography
            component={patientId ? RouterLink : 'div'}
            to={patientId ? `/patient/${patientId}` : undefined}
            variant="h6"
            onClick={(clickEvent: React.MouseEvent) => {
              if (patientId) {
                clickEvent.stopPropagation();
              }
            }}
            sx={{
              color: ELIG_TOKENS.ink,
              display: 'block',
              fontWeight: 650,
              lineHeight: 1.25,
              textDecoration: 'none',
              '&:hover': patientId
                ? {
                    color: ELIG_TOKENS.ink,
                    textDecoration: 'underline',
                    textUnderlineOffset: '3px',
                  }
                : undefined,
            }}
          >
            {event.title}
          </Typography>
          {patientId && phoneRaw && (
            <Link
              href={`tel:${phoneRaw}`}
              aria-label={`Call ${phoneDisplay}`}
              onClick={(clickEvent: React.MouseEvent) => {
                clickEvent.stopPropagation();
                tracking.appointment.popoverPhoneClicked({
                  appointment_id: appointmentId,
                  patient_id: patientId,
                });
              }}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                mt: 0.25,
                fontSize: 13,
                color: ELIG_TOKENS.inkSoft,
                textDecoration: 'none',
                '&:hover': {
                  color: ELIG_TOKENS.ink,
                  textDecoration: 'underline',
                  textUnderlineOffset: '3px',
                },
              }}
            >
              <PhoneOutlined sx={{ fontSize: 14 }} />
              {phoneDisplay}
            </Link>
          )}
          <Box sx={{ mt: 0.5 }}>
            <AppointmentPropertiesTable
              appointment={event.resource}
              rows={[
                'status',
                'visit_status',
                'provider',
                'type',
                'telehealth',
                'labels',
                'time',
                'notes',
              ]}
              interactiveRows={['status', 'visit_status', 'provider', 'type', 'labels', 'notes']}
              editorMode="floating"
              onEditorInteraction={onEditorInteraction}
            />
          </Box>
        </Box>
      </Box>
      {model.actionMessage && (
        <Box
          sx={{
            bgcolor: model.accentColor,
            color: '#fff',
            px: 3,
            py: 1.25,
            fontSize: 12,
            fontWeight: 600,
            lineHeight: 1.4,
            borderBottomLeftRadius: '7px',
            borderBottomRightRadius: '7px',
          }}
        >
          {model.actionMessage}
        </Box>
      )}
    </Box>
  );
};

const isAppointmentPropertyEditorSurface = (target: EventTarget | null): boolean =>
  target instanceof Element &&
  target.closest('[data-appointment-property-editor-surface="true"]') !== null;

const isCalendarV2EventBlock = (target: EventTarget | null): boolean =>
  target instanceof Element && target.closest('[data-calendar-v2-event-block="true"]') !== null;

const getLegacyReality = (
  event: CalendarEvent,
): 'real' | 'tentative' | 'cancelled' | 'suggested' | 'rescheduling' | 'block' => {
  if (event.resource.status === 'suggested') return 'suggested';
  if (event.resource.confirmation_status === 'cancelled') return 'cancelled';
  if (event.resource.confirmation_status === 'reschedule_requested') return 'rescheduling';
  return 'real';
};

const getVariant = (reality: string): 'standard' | 'suggested' | 'actionable' | 'block' => {
  if (reality === 'suggested') return 'suggested';
  if (reality === 'cancelled' || reality === 'rescheduling') return 'actionable';
  if (reality === 'block') return 'block';
  return 'standard';
};

const getActionMessage = (reality: string): string | null => {
  if (reality === 'cancelled') return 'Cancelled appointment';
  if (reality === 'rescheduling') return 'Patient requested a different time';
  if (reality === 'suggested') return 'Suggested opening';
  return null;
};

const opaqueTint = (hex: string, alpha: number): string => {
  if (!/^#[0-9a-fA-F]{6}$/.test(hex)) return 'rgba(247, 248, 252, 0.98)';
  const amount = 1 - alpha;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const mix = (channel: number) => Math.round(channel + (255 - channel) * amount);
  return `rgb(${mix(r)}, ${mix(g)}, ${mix(b)})`;
};
